import type { BlogPost } from './blogTypes';
import { commonLinks, ctaDemo, ctaTrial, ctaAgents } from './blogCommonLinksAndCtas';

export const integrationsLaunchBlogPosts: BlogPost[] = [
{
  slug: 'shopify-delivery-management-integration-sync-orders-to-dispatch',
  title: 'Shopify Delivery Management Integration: Sync Orders to Dispatch Automatically',
  excerpt: 'How a Shopify order becomes a dispatched, tracked, and proven delivery with zero manual entry: webhook in, Captain assigned, status written back, tracking link in the customer email.',
  date: '2026-05-06',
  category: 'Integrations',
  author: 'Rohan Kumar',
  readTime: '10 min read',
  image: 'https://cdn.geofleet.ai/blog-hero-shopify-dispatch.png',
  intro: 'A practical walkthrough of the Geofleet Shopify delivery management integration: what fires when an order is placed, how fields map, how edits and cancellations are handled, and what to measure once it is live.',
  seo: {
    documentTitle: 'Shopify Delivery Management Integration | Geofleet',
    metaDescription: 'Connect Shopify to dispatch: orders sync automatically, a driver is assigned, status writes back to Shopify, and customers get a branded tracking link.',
  },
  keyTakeaways: [
    { lead: 'The order webhook is the trigger; everything after it is automatic.', text: 'A paid Shopify order becomes a Geofleet order within seconds, auto allocation assigns a Captain, and the store never sees a manual copy-paste step.' },
    { lead: 'Status write-back is what makes the integration trustworthy.', text: 'Fulfilled, out for delivery, and delivered are written back to the Shopify order, so support staff and customers see the same truth in the store they already use.' },
    { lead: 'Field mapping decides how good your first week is.', text: 'Address lines, delivery notes, time windows, and COD amounts all have a home in Geofleet; confirm each one before you switch on live traffic.' },
    { lead: 'Edits and cancellations must flow, not just new orders.', text: 'A changed address or a cancelled order that does not reach the Captain is the most common failure in any Shopify local delivery setup.' },
    { lead: 'Measure dispatch lag, first-attempt success, and support tickets per order.', text: 'Those three numbers tell you whether the integration is doing its job better than the spreadsheet or the copy-paste routine it replaced.' },
  ],
  body: [],
  sections: [
    {
      type: 'p',
      text: "Most Shopify stores that deliver locally start the same way. An order comes in, someone reads it off the admin screen, types the address into a delivery app or a WhatsApp group, and later marks the order fulfilled by hand. It works at twenty orders a day. At a hundred it produces typos, missed orders, and a support inbox full of 'where is my delivery?'. A Shopify delivery management integration removes that human relay: the order flows into dispatch on its own, a driver is assigned on its own, and the status flows back on its own.",
    },
    {
      type: 'p',
      text: "This guide walks through exactly how that works with Geofleet, from the moment Shopify fires an order webhook to the moment proof of delivery lands back on the order. It covers setup in the Integrations settings card, field mapping, edits and cancellations, multi-location stores, and the handful of numbers worth watching after go-live.",
    },

    {
      type: 'h2',
      text: 'Why a Shopify delivery management integration beats manual order entry',
    },
    {
      type: 'p',
      text: "The case is not really about speed, although speed matters. It is about removing a class of errors that only exist because a person is retyping data. Every manual step between the checkout and the driver is a place where an apartment number gets dropped, a phone number loses a digit, or an order is simply forgotten during a rush.",
    },
    {
      type: 'ul',
      items: [
        'No retyping: the address, phone, line items, and notes arrive exactly as the customer entered them.',
        'No dispatch lag: auto allocation can assign a Captain within moments of payment instead of when someone next checks the admin.',
        'No status drift: the Shopify order reflects the real delivery state, so support does not have to ask the dispatcher.',
        'Customer messaging stays in Shopify: the tracking link rides along in the shipping confirmation the store already sends.',
        'One source of truth for finance: delivered timestamps and proof of delivery sit against the order, not in a chat thread.',
      ],
    },
    {
      type: 'p',
      text: "If you are still deciding whether the native Shopify local delivery app is enough, there is a section on that further down. Short version: it is fine for a single store with one or two drivers, and it runs out of road quickly once you need routing, automatic assignment, or cash handling.",
    },

    {
      type: 'h2',
      text: 'What happens when an order arrives: the end-to-end flow',
    },
    {
      type: 'h3',
      text: '1. Order webhook to Geofleet order',
    },
    {
      type: 'p',
      text: "When a Shopify order is created (or paid, depending on the trigger you choose), Shopify sends a webhook to Geofleet. Geofleet creates a delivery order carrying the Shopify order number, customer name and phone, shipping address, line items, any delivery instructions from the checkout notes field, and the payment state. The Shopify order ID is stored as the external reference so every later update can be matched back without ambiguity.",
    },
    {
      type: 'h3',
      text: '2. Auto allocation assigns a Captain',
    },
    {
      type: 'p',
      text: "With Auto Allocation (included from Premium) enabled under Planning and Automation, the new order is evaluated against the active Captains, their current load, vehicle constraints, service zone, and the delivery window. The best match receives the job on the driver app. If your operation batches deliveries into waves, the order waits in the planning pool and the Smart Planner builds it into the next route instead. Either way, nobody has to look at a screen and pick a name. The mechanics of how orders get assigned without a dispatcher are covered in a separate post linked below.",
    },
    {
      type: 'h3',
      text: '3. Status write-back to Shopify',
    },
    {
      type: 'p',
      text: "This is the step that makes the integration bidirectional. As the Captain progresses, Geofleet updates the Shopify order: a fulfillment is created when the order is assigned or picked up, the shipment status moves to out for delivery when the Captain starts the drop, and it moves to delivered on completion. A failed attempt is surfaced as an attempted delivery event rather than silently leaving the order open.",
    },
    {
      type: 'h3',
      text: '4. Branded tracking link in Shopify customer notifications',
    },
    {
      type: 'p',
      text: "Geofleet passes a tracking URL back with the fulfillment. Shopify includes that URL in its shipping confirmation and out-for-delivery emails automatically, so you do not have to build a separate notification flow. Because the link uses your Tracking URL Configuration, the page the customer opens carries your store branding, the live map, and the ETA, not a third-party logo.",
    },
    {
      type: 'h3',
      text: '5. Proof of delivery closes the loop',
    },
    {
      type: 'p',
      text: "On completion, the Captain captures a photo, signature, or OTP as configured. The proof of delivery is attached to the Geofleet order and the delivered event is written to Shopify with the timestamp. If a customer disputes a delivery later, support opens the order in either system and sees the same evidence.",
    },
    {
      type: 'quote',
      text: "We used to have one person whose entire morning was copying orders from Shopify into the driver group. Now that person handles exceptions instead, and the exceptions are fewer because nobody is retyping addresses.",
      attribution: 'Operations manager, D2C grocery brand',
    },

    {
      type: 'infographic',
      image: '/images/blog/blog-info-shopify-dispatch.svg',
      alt: 'Four numbered steps on a rail: order placed as the webhook fires, auto dispatched with a Captain assigned, status written back to the Shopify order, and delivered with photo, OTP, and timestamp proof; nothing re-typed by hand',
      caption: 'Order to doorstep in four steps. The webhook creates and dispatches the delivery; the status write-back keeps Shopify and the customer notification in sync.',
    },

    {
      type: 'h2',
      text: 'Setting it up in the Integrations settings card',
    },
    {
      type: 'p',
      text: "The connection lives under Developer, then Integrations, where Shopify has its own card alongside SMS gateways and other connectors. The setup itself is short; the time goes into confirming the mapping and running test orders.",
    },
    {
      type: 'ol',
      items: [
        'Open the Shopify card in Integrations and connect your store. You will authorise Geofleet to read orders and write fulfillments.',
        'Choose the trigger: order created, or order paid. Most stores that take COD choose created; card-only stores usually choose paid to avoid dispatching unpaid carts.',
        'Pick which orders qualify: by shipping method (for example, only orders where the customer selected local delivery), by location, or by tag.',
        'Review the field mapping (next section) and set defaults for anything Shopify does not send, such as a default delivery window or service type.',
        'Enable status write-back and confirm the tracking URL template under Customization, then Tracking URL Configuration.',
        'Place two or three test orders in a development store or with a test tag, watch them arrive, get assigned, and complete, then check the Shopify order timeline.',
      ],
    },
    {
      type: 'callout',
      variant: 'tip',
      title: 'Filter before you dispatch',
      text: "Not every Shopify order is a delivery. Pickup orders, gift cards, and orders shipped by a national carrier should never reach the dispatch pool. Scope the integration by shipping method or tag on day one so the Captains only ever see real local deliveries.",
    },

    {
      type: 'h2',
      text: 'Field mapping: what moves from Shopify to Geofleet',
    },
    {
      type: 'p',
      text: "Shopify's order object is rich, but delivery only needs a subset of it. The table shows the fields most operations care about and where they land. The ones that cause trouble are the ones Shopify does not enforce: delivery notes, time windows, and the second address line.",
    },
    {
      type: 'table',
      caption: 'Core field mapping for a Shopify delivery management integration',
      headers: ['Shopify field', 'Geofleet field', 'Notes'],
      rows: [
        ['Order number / ID', 'External reference', 'Used for matching every later update and for the tracking page'],
        ['Shipping address (lines 1-2, city, postcode)', 'Delivery address', 'Geocoded on arrival; AI address cleanup flags unit numbers hidden in line 2 or notes'],
        ['Customer phone / email', 'Recipient contact', 'Drives SMS and email notifications; phone format is normalised'],
        ['Order note / delivery instructions', 'Delivery notes', 'Shown to the Captain on the stop card, for example gate codes'],
        ['Requested delivery date or slot (app or checkout field)', 'Time window', 'Comes from a checkout extension or line-item property; set a default if absent'],
        ['Financial status + payment gateway', 'COD amount', 'Cash on delivery orders carry the amount to collect; paid orders carry zero'],
        ['Line items and quantities', 'Package contents', 'Used for load planning and shown on the proof of delivery'],
        ['Location ID', 'Pickup hub', 'Multi-location stores map each Shopify location to a Geofleet hub'],
      ],
    },
    {
      type: 'h3',
      text: 'Time windows',
    },
    {
      type: 'p',
      text: "Shopify has no native delivery slot field. Stores typically capture it with a date picker app or a checkout extension, and it lands as an order attribute or line-item property. Map that attribute to the Geofleet time window during setup. If a store has no slot picker, set a default window (for example same day before 8pm) so auto allocation has something to plan against.",
    },
    {
      type: 'h3',
      text: 'Cash on delivery',
    },
    {
      type: 'p',
      text: "When the payment gateway is COD, the order total becomes the amount to collect. The Captain sees it on the stop, records the collection, and cash management reconciles it against the order at end of day. If the order is partially paid or a refund was issued in Shopify before delivery, the amount is recalculated. The COD operations guide linked below covers what happens on the reconciliation side.",
    },

    {
      type: 'h2',
      text: 'Handling edits, cancellations, and multi-location stores',
    },
    {
      type: 'h3',
      text: 'Order edits',
    },
    {
      type: 'p',
      text: "Customers change their minds. Shopify sends an order updated webhook when an address, phone, or line item changes. Geofleet applies the change to the delivery order and, if the order is already assigned, pushes an update to the Captain's stop. An address change large enough to move the stop out of the Captain's zone triggers re-allocation rather than a silent update.",
    },
    {
      type: 'h3',
      text: 'Cancellations',
    },
    {
      type: 'p',
      text: "A cancelled Shopify order cancels the delivery. If the Captain has not started the stop, it disappears from their route. If they are already en route, the stop is flagged as cancelled with an alert so dispatch can decide whether to recall the parcel. Either way the cancellation is logged against the order so nobody delivers a cancelled item and then argues about the refund.",
    },
    {
      type: 'h3',
      text: 'Multi-location stores',
    },
    {
      type: 'p',
      text: "Shopify assigns each fulfillment to a location. Map every location to a Geofleet hub, and the integration will route the order to the right pickup point automatically. Captains are scoped to hubs, so a Captain based at the north store only sees orders fulfilled from the north store. This is also how a single brand with three city stores keeps three separate dispatch pools without three separate setups. For larger networks, the multi-hub scaling guide linked at the end goes deeper.",
    },
    {
      type: 'p',
      text: "One more detail that matters here: Shopify may deliver the same webhook more than once. Geofleet de-duplicates on the Shopify order ID, so a repeated webhook updates the existing delivery rather than creating a second one. If you have ever seen two drivers turn up for one order, that is the bug this prevents.",
    },

    {
      type: 'h2',
      text: 'What to measure after go-live',
    },
    {
      type: 'p',
      text: "Give the integration two weeks of live traffic, then compare a small set of numbers against the manual process it replaced. The reports and analytics module has all of these, but they are worth pulling out on their own.",
    },
    {
      type: 'ul',
      items: [
        'Dispatch lag: time from Shopify order created to Captain assigned. Manual processes usually sit at 20-60 minutes; the integration should bring it to a few minutes or less.',
        'First-attempt delivery success: the share of orders delivered on the first visit. Cleaner addresses and notes should push this up.',
        'Support tickets per 100 orders tagged delivery status: with tracking links in the Shopify emails, this should fall.',
        'Orders with missing or invalid addresses caught before dispatch: a proxy for how much AI address cleanup is saving you.',
        'Status write-back failures: should be near zero; anything above that points to a permission or mapping problem.',
      ],
    },
    {
      type: 'callout',
      variant: 'stat',
      title: 'Illustrative example',
      value: '30 min to 2 min',
      text: "A 150-order-a-day store that moved from copying orders by hand to the Shopify integration might see median dispatch lag fall from roughly half an hour to a couple of minutes. The exact figure depends on your trigger and allocation rules; the point is that the queue of unassigned orders stops building up during peak hours.",
    },

    {
      type: 'h2',
      text: 'Is a Shopify local delivery app enough?',
    },
    {
      type: 'p',
      text: "Shopify's built-in local delivery and the lightweight Shopify local delivery apps in the app store cover the basics: a delivery radius, a flat fee, and a simple driver list. They are a fair starting point for a single store doing a handful of drops a day. They do not do automatic assignment, route planning across multiple drivers, live tracking pages under your brand, proof of delivery with signatures and OTP, or cash on delivery reconciliation.",
    },
    {
      type: 'p',
      text: "The trigger to move up is usually one of three things: a second driver, a second location, or the first serious dispute over whether a parcel was delivered. At that point a dispatch platform connected to Shopify is the pragmatic step, and the setup above takes an afternoon, not a project. If your order data also lives in an ERP or a spreadsheet, the comparison post on choosing an order source will help you decide what to connect first.",
    },

    {
      type: 'relatedReading',
      heading: 'Related reading',
      links: [
        { label: 'Auto allocation explained: orders assigned without a dispatcher', to: '/blog/auto-allocation-explained-orders-assigned-without-a-dispatcher' },
        { label: 'Shopify vs Acumatica vs Google Sheets: which order source fits?', to: '/blog/shopify-vs-acumatica-vs-google-sheets-order-source-for-delivery' },
        { label: 'White-label delivery tracking: branded pages that build loyalty', to: '/blog/white-label-delivery-tracking-custom-tracking-url-and-terminology' },
        { label: 'Cash on delivery without headaches', to: '/blog/cash-on-delivery-cod-without-headaches-complete-operations-guide' },
        { label: 'E-commerce delivery solutions', to: '/industries/ecommerce' },
      ],
    },
    {
      type: 'ctaRow',
      items: [ctaTrial, ctaDemo],
    },
  ],
  faq: [
    {
      question: 'How does the Geofleet Shopify integration get orders from my store?',
      answer: "Shopify sends an order webhook to Geofleet when an order is created or paid, depending on the trigger you choose. Geofleet creates a delivery order with the address, contact details, line items, notes, and payment state, and stores the Shopify order ID as the external reference so later updates match correctly.",
    },
    {
      question: 'Does delivery status sync back to Shopify?',
      answer: "Yes. Geofleet creates a fulfillment on the Shopify order and updates it as the Captain progresses: out for delivery when the drop starts, delivered on completion, and an attempted-delivery event if the stop fails. The branded tracking URL is attached to the fulfillment so Shopify includes it in its own customer emails.",
    },
    {
      question: 'Can I use it with more than one Shopify location?',
      answer: "Yes. Each Shopify location is mapped to a Geofleet hub. Orders are routed to the hub matching their fulfillment location, and Captains scoped to that hub are the only ones considered for auto allocation.",
    },
    {
      question: 'What happens if a customer edits or cancels an order after it is dispatched?',
      answer: "Shopify sends an update or cancel webhook. Address and contact edits are pushed to the Captain's stop; a large address change triggers re-allocation. A cancellation removes the stop if it has not started and flags it with an alert if the Captain is already on the way.",
    },
    {
      question: 'How are cash on delivery orders handled?',
      answer: "When the Shopify payment gateway is COD, the order total is mapped to the Geofleet COD amount. The Captain sees the amount to collect, records it on delivery, and cash management reconciles collections against orders at the end of the shift.",
    },
    {
      question: 'Is a Shopify local delivery app enough for a small store?',
      answer: "For one store with one or two drivers and a simple radius, the native local delivery feature is often sufficient. Once you need automatic assignment, multi-driver routing, branded live tracking, proof of delivery, or COD reconciliation, a dispatch platform connected to Shopify is the practical next step.",
    },
  ],
},
{
  slug: 'acumatica-last-mile-delivery-integration-erp-to-dispatch',
  title: 'Acumatica Last-Mile Integration: Closing the Loop Between ERP and Delivery',
  excerpt: 'How an Acumatica delivery integration moves sales orders and shipments into dispatch, executes them, and writes proof of delivery, timestamps, distance, and exceptions back so finance can invoice and close the shipment.',
  date: '2026-05-20',
  category: 'Integrations',
  author: 'Avni Agarwal',
  readTime: '11 min read',
  image: 'https://cdn.geofleet.ai/blog-hero-acumatica-erp.png',
  intro: 'For distributors and manufacturers who run the business in Acumatica, the delivery leg is the last place data still lives on paper. This guide shows how the Geofleet Acumatica delivery integration closes that gap in both directions, and why the write-back is the part finance will thank you for.',
  seo: {
    documentTitle: 'Acumatica Delivery Integration for Last Mile | Geofleet',
    metaDescription: 'Connect Acumatica to dispatch: shipments flow to drivers; proof of delivery, timestamps, and exceptions flow back to trigger invoicing and close the shipment.',
  },
  keyTakeaways: [
    { lead: 'Acumatica stays the system of record; dispatch becomes the execution layer.', text: 'Sales orders and shipments originate in the ERP, Geofleet runs the physical delivery, and nothing is re-keyed in either direction.' },
    { lead: 'The write-back is the whole point.', text: 'Delivered timestamps, proof of delivery, distance, and exceptions return to the Acumatica shipment so invoicing can trigger on delivery rather than on dispatch.' },
    { lead: 'Partial deliveries and returns need a plan before go-live.', text: 'A short-shipped line or a rejected pallet has to produce the right Acumatica document, not a note in a chat thread.' },
    { lead: 'Warehouse-to-route handoff is where most ERP-first rollouts stumble.', text: 'Decide whether Acumatica confirms the shipment before or after the Captain scans it out, and build the flow around that answer.' },
    { lead: 'Reconciliation gets simpler when both systems share one reference.', text: 'The Acumatica shipment number on every Geofleet order means finance, dispatch, and customer service are all looking at the same key.' },
  ],
  body: [],
  sections: [
    {
      type: 'p',
      text: "Companies that run on Acumatica have usually already solved order entry, inventory, and invoicing. What they often have not solved is the last mile: the shipment leaves the warehouse and, from that moment until a signed delivery note is scanned back in days later, the ERP knows nothing. An Acumatica delivery integration fixes that by treating dispatch as an extension of the shipment record rather than a separate world with its own spreadsheet.",
    },
    {
      type: 'p',
      text: "This post is written for ERP-first buyers: the operations director or finance lead who is not shopping for a delivery app so much as looking for the delivery leg to stop being a black hole. It covers how sales orders and shipments flow into Geofleet, how the warehouse handoff works, what comes back to Acumatica, and how partials, returns, and reconciliation are handled. If you are evaluating integration approaches more broadly, the no-code ERP guide linked at the end compares native connectors with MCP, webhooks, and CSV.",
    },

    {
      type: 'h2',
      text: 'Why ERP-first teams need an Acumatica delivery integration',
    },
    {
      type: 'p',
      text: "The symptom is familiar. Invoices go out when the shipment is confirmed, not when the customer signs, so disputes about short deliveries become credit notes weeks later. Customer service cannot answer 'where is my order?' without calling the driver. Delivery distance for freight recharges is estimated rather than measured. Each of these is a data gap between the ERP and the road.",
    },
    {
      type: 'ul',
      items: [
        'Invoice timing: billing on delivery confirmation instead of shipment confirmation reduces disputes and aligns revenue with the actual event.',
        'Acumatica shipment tracking: sales reps and customer service see live status in the ERP screen they already use, with a customer-facing tracking link.',
        'Measured distance: GPS-based distance on each delivery feeds freight recharges and Captain payouts instead of a flat estimate.',
        'Exception visibility: refused, partial, and failed deliveries appear on the Acumatica shipment the same day, not when paperwork arrives.',
        'One reference everywhere: the shipment number is the key on the Geofleet order, the proof of delivery, and the tracking page.',
      ],
    },
    {
      type: 'p',
      text: "None of this requires replacing Acumatica or moving order management out of it. The ERP remains the system of record. Geofleet handles what Acumatica does not do: allocation to Captains, route planning, the driver app, live tracking, and proof capture. This division of labour is the same one described in the broader post on Acumatica logistics and last-mile technology stacks.",
    },

    {
      type: 'h2',
      text: 'The loop: from sales order to closed shipment',
    },
    {
      type: 'h3',
      text: 'Sales order and shipment out of Acumatica',
    },
    {
      type: 'p',
      text: "The trigger is the Acumatica shipment, not the sales order. A sales order can be split across several shipments and dates, and the shipment is the document that represents 'these lines, from this warehouse, on this day'. When a shipment reaches the status you choose (typically Open or Confirmed), the integration creates a Geofleet order carrying the customer, ship-to address and contact, shipment lines and quantities, requested date, freight terms, and any delivery instructions from the order.",
    },
    {
      type: 'h3',
      text: 'Warehouse-to-route handoff',
    },
    {
      type: 'p',
      text: "This is the step to design carefully. Two patterns work. In the first, Acumatica confirms the shipment when picking is complete, the Geofleet order is created, and the Captain scans the shipment label at loading to mark it picked up. In the second, the Geofleet order is created earlier (at Open) so routes can be planned the night before, and the Captain's scan at loading is what confirms the shipment in Acumatica. The second pattern gives planners more lead time; the first is simpler to audit. Choose one and make the Shipping Label Configuration match, so the barcode on the packing slip is the same one the driver app expects.",
    },
    {
      type: 'h3',
      text: 'Execution in Geofleet',
    },
    {
      type: 'p',
      text: "From here the delivery runs like any other Geofleet order. Auto allocation or the Smart Planner assigns the order to a Captain and sequences the route. The customer gets a branded tracking link. The Captain follows the route, captures proof of delivery per line if you need it, and records any exception: refused, partial, wrong address, not home. B2B deliveries often need a name and signature from a receiving clerk plus a photo of the pallet on the dock; both are standard proof options.",
    },
    {
      type: 'h3',
      text: 'Write-back to Acumatica',
    },
    {
      type: 'p',
      text: "On completion, Geofleet writes back to the Acumatica shipment: delivered timestamp, proof of delivery (signature, photo, and the name of the person who signed), GPS-measured distance, delivered quantities per line, and any exception codes. Depending on your configuration, the delivered event can move the shipment to Completed and release the invoice through the normal Acumatica prepare-invoice process, or it can update custom fields and leave the release to a finance user.",
    },
    {
      type: 'quote',
      text: "Our controller's rule was simple: no signature, no invoice. Before the integration that meant waiting for paper. Now the signature is on the shipment within a minute of the drop, and invoicing runs the same evening.",
      attribution: 'Operations director, regional building materials distributor',
    },

    {
      type: 'infographic',
      image: '/images/blog/blog-info-acumatica-erp.svg',
      alt: 'Four-node loop between Acumatica and Geofleet: sales order created in the ERP, shipment sent to dispatch, delivery and proof of delivery executed by the Captain, then invoice closed by finance, with nothing re-keyed in either direction',
      caption: 'The ERP-to-dispatch loop. The right side is where the value is: delivery evidence returns to Acumatica and triggers the finance close.',
    },

    {
      type: 'h2',
      text: 'What flows back, and why finance cares',
    },
    {
      type: 'p',
      text: "Most delivery integrations stop at status. An ERP-grade integration has to return the fields that finance and customer service act on. The table lists what Geofleet writes back to the Acumatica shipment and what each field is used for downstream.",
    },
    {
      type: 'table',
      caption: 'Write-back fields from Geofleet to the Acumatica shipment',
      headers: ['Field written back', 'Where it lands in Acumatica', 'What it enables'],
      rows: [
        ['Delivered timestamp', 'Shipment date/time, custom delivered-on field', 'Invoice on delivery; on-time reporting against the promised date'],
        ['Proof of delivery (signature, photo, signer name)', 'Attachment on the shipment, link on the invoice', 'Dispute resolution; customer portal access to the POD'],
        ['Delivered quantity per line', 'Shipment line quantities', 'Short-ship handling, back-order creation, accurate invoicing'],
        ['Exception code and note', 'Shipment custom field, optional case', 'Refusals, damages, and failed attempts surface the same day'],
        ['GPS-measured distance', 'Freight cost field or custom attribute', 'Freight recharges, Captain payout, route cost analysis'],
        ['Tracking URL', 'Shipment tracking number / URL field', 'Acumatica shipment tracking visible to sales and service'],
        ['Captain and vehicle', 'Custom fields', 'Audit trail and carrier-style reporting'],
      ],
    },
    {
      type: 'callout',
      variant: 'info',
      title: 'Why the timestamp matters more than the status',
      text: "A status tells you what happened. A timestamp lets you prove when, which is what revenue recognition, SLA credits, and demurrage-style waiting charges all depend on. Make sure the delivered time written back is the Captain's on-site completion time, not the time the sync job ran.",
    },

    {
      type: 'h2',
      text: 'B2B distribution use cases',
    },
    {
      type: 'p',
      text: "The mechanics above are the same across industries, but the details that matter differ. Three common Acumatica logistics profiles illustrate the range.",
    },
    {
      type: 'h3',
      text: 'Building supplies',
    },
    {
      type: 'p',
      text: "Deliveries are heavy, often to construction sites with no formal address, and frequently need a crane or forklift at the far end. Site contact and delivery instructions from the Acumatica order have to reach the Captain intact, and the proof of delivery usually needs a photo of the load on site plus a signature from the site foreman. Partial deliveries are routine when a truck cannot take a full order in one trip. See the building supplies industry page for the full picture.",
    },
    {
      type: 'h3',
      text: 'Automotive parts',
    },
    {
      type: 'p',
      text: "High-frequency, low-value drops to workshops, sometimes several a day to the same customer. What matters is speed from shipment confirmation to dispatch and accurate line-level delivery so warranty and core returns reconcile. Returns pickups are often bundled with the next delivery, which means the Geofleet order needs a pickup line as well as drop lines.",
    },
    {
      type: 'h3',
      text: 'Manufacturing',
    },
    {
      type: 'p',
      text: "Scheduled deliveries to a small number of large customers, frequently with strict dock windows and proof requirements defined in the customer contract. The value here is less about routing and more about the write-back: delivered timestamps and signed PODs feeding invoicing and on-time reporting per customer. The manufacturing industry page covers the wider operating model.",
    },

    {
      type: 'h2',
      text: 'Partial deliveries, returns, and reconciliation',
    },
    {
      type: 'h3',
      text: 'Partial deliveries',
    },
    {
      type: 'p',
      text: "When a Captain delivers fewer units than the shipment line, the delivered quantity is written back per line and the shortfall is flagged. Your Acumatica configuration then decides what happens: generate a back-order shipment for the balance, invoice only the delivered quantity, or hold the invoice for review. The key is that the decision is made once, in configuration, rather than per incident by a person on the phone.",
    },
    {
      type: 'h3',
      text: 'Returns and refusals',
    },
    {
      type: 'p',
      text: "A refused delivery is an exception, not a completed delivery, and it comes back to Acumatica that way with the reason and photo. A planned return pickup is created from the ERP as its own document (an RMA or return shipment) and flows into Geofleet as a pickup order, so the reverse leg has the same tracking and proof as the forward leg. The reverse logistics post linked below covers pickup windows and COD refunds in more depth.",
    },
    {
      type: 'h3',
      text: 'Reconciliation',
    },
    {
      type: 'p',
      text: "Because every Geofleet order carries the Acumatica shipment number, end-of-day reconciliation is a join, not a hunt. Delivered shipments should have a delivered timestamp, a POD, and a released invoice. Anything missing one of those three is an exception list, and a short one. Add cash on delivery and the same join covers collected amounts against the Acumatica AR document.",
    },
    {
      type: 'callout',
      variant: 'tip',
      title: 'Run the three-column check daily',
      text: "For every shipment marked delivered today: is there a timestamp, is there a POD attachment, is the invoice released? A daily list of rows failing any one of those tells you within 24 hours whether the integration or the process has a gap.",
    },

    {
      type: 'h2',
      text: 'Acumatica shipment tracking for customers and sales reps',
    },
    {
      type: 'p',
      text: "The write-back includes the tracking URL, which means Acumatica's own shipment screen becomes a tracking screen. A sales rep looking at a customer's order sees where the truck is without opening another system. The same URL, configured under Tracking URL Configuration to use your domain and branding, goes to the customer via the Acumatica notification or Geofleet's own SMS and email templates. Choose one channel to avoid double notifications; most teams let Geofleet send delivery-stage messages and keep order confirmations in the ERP.",
    },

    {
      type: 'h2',
      text: 'Rollout checklist',
    },
    {
      type: 'ol',
      items: [
        'Agree the trigger status (Open or Confirmed) and the handoff pattern with the warehouse lead before anything is configured.',
        'Map ship-to addresses and contacts; clean the worst offenders with AI address cleanup before the first live day.',
        'Decide the write-back policy for partials and holds in Acumatica, and test it with a deliberately short-shipped order.',
        'Configure proof of delivery requirements per customer class, for example signature plus photo for site deliveries.',
        'Run a week in parallel with the existing paper process, reconciling the three-column check each evening.',
        'Cut over one warehouse or one route group first, then expand once write-back failures are at zero for a week.',
      ],
    },
    {
      type: 'p',
      text: "Expect the integration itself to be the smaller half of the work. The larger half is the process decisions above, and the good news is that they are decisions your team already knows how to make.",
    },

    {
      type: 'relatedReading',
      heading: 'Related reading',
      links: [
        { label: 'How to connect your ERP to a delivery platform without custom code', to: '/blog/connect-erp-to-delivery-platform-without-custom-code' },
        { label: 'B2B distribution delivery', to: '/industries/b2b-distribution' },
        { label: 'Building supplies delivery', to: '/industries/building-supplies' },
        { label: 'Manufacturing delivery', to: '/industries/manufacturing' },
        { label: 'Reverse logistics: returns, pickup windows, and COD', to: '/blog/reverse-logistics-returns-pickup-windows-cod' },
      ],
    },
    {
      type: 'ctaRow',
      items: [ctaDemo, ctaTrial],
    },
  ],
  faq: [
    {
      question: 'Does the Acumatica integration use the sales order or the shipment?',
      answer: "The shipment. A sales order can be split across multiple shipments and dates, and the shipment represents the specific lines leaving a specific warehouse on a specific day. Geofleet creates a delivery order when the shipment reaches the status you configure, usually Open or Confirmed.",
    },
    {
      question: 'What data is written back to Acumatica after delivery?',
      answer: "Delivered timestamp, proof of delivery (signature, photo, signer name), delivered quantity per line, exception codes and notes, GPS-measured distance, the tracking URL, and the Captain and vehicle. These land on the shipment record and its attachments, and can trigger the invoice release.",
    },
    {
      question: 'Can invoicing be triggered by delivery instead of shipment confirmation?',
      answer: "Yes. When the delivered event is written back, the shipment can move to Completed and release the invoice through the standard Acumatica prepare-invoice flow. Alternatively the integration updates fields only and a finance user releases invoices in a batch.",
    },
    {
      question: 'How are partial deliveries handled?',
      answer: "Delivered quantities are written back per line and the shortfall is flagged. Acumatica configuration then decides whether to create a back-order shipment for the balance, invoice only what was delivered, or hold the invoice for review.",
    },
    {
      question: 'Does it support returns and pickups?',
      answer: "Yes. A return document in Acumatica (RMA or return shipment) flows into Geofleet as a pickup order with the same tracking and proof of delivery as an outbound delivery. Refused deliveries are written back as exceptions with the reason and photo.",
    },
    {
      question: 'Do we need developers to set up the Acumatica delivery integration?',
      answer: "Not for the standard flow. The native connector handles shipment ingestion and write-back with configuration rather than code. Custom fields or unusual document flows may need an Acumatica administrator to expose them; heavier customisation can use webhooks and the batch API.",
    },
  ],
},
{
  slug: 'google-sheets-delivery-dispatch-lightweight-oms',
  title: 'Google Sheets as a Lightweight OMS: Dispatching Deliveries From a Spreadsheet',
  excerpt: 'If your orders already live in a Google Sheet, you can dispatch straight from it. The column template, how rows become deliveries, how status and ETA write back, and the data traps to avoid.',
  date: '2026-06-03',
  category: 'Integrations',
  author: 'Shuvam',
  readTime: '10 min read',
  image: 'https://cdn.geofleet.ai/blog-hero-sheets-dispatch.png',
  intro: 'A hands-on guide to using Google Sheets as a delivery route planner and order source with Geofleet: the exact columns, the ingest and write-back loop, when a sheet is enough, and when it is time to move up.',
  seo: {
    documentTitle: 'Google Sheets Delivery Route Planner | Geofleet',
    metaDescription: 'Turn a Google Sheet into a delivery route planner: column template, automatic ingest into dispatch, status and ETA written back to the sheet, and data traps.',
  },
  keyTakeaways: [
    { lead: 'A sheet is a valid order source if the columns are disciplined.', text: 'Seven columns (order id, customer, phone, address, window, COD, notes) are enough for auto allocation, routing, and tracking to work.' },
    { lead: 'The write-back is what makes it a system rather than a to-do list.', text: 'Status, ETA, Captain, and a proof of delivery link return to the same row, so the sheet the team already watches becomes the live dispatch view.' },
    { lead: 'Most failures are data-quality failures.', text: 'Bad addresses, merged cells, and inconsistent phone formats cause more missed deliveries than any routing decision. AI address cleanup catches most of them before dispatch.' },
    { lead: 'Sheets scale further than people expect, but not forever.', text: 'Multiple editors, multi-hub operations, and returns are the usual signals to move to an OMS or a native e-commerce or ERP connector.' },
    { lead: 'The migration path is incremental.', text: 'Keep the sheet as the order source, let Geofleet handle execution, and swap the source later without changing how Captains work.' },
  ],
  body: [],
  sections: [
    {
      type: 'p',
      text: "A large share of small and mid-sized delivery operations run on a Google Sheet. Orders come in by phone, WhatsApp, email, or a web form and land in rows; a dispatcher reads the rows and calls drivers. It is fragile, but it is also honest: the sheet is the order management system, and everyone knows how to use it. Using Google Sheets as a delivery route planner does not require abandoning that. It requires connecting it, so rows turn into dispatched deliveries and the delivery results land back in the same rows.",
    },
    {
      type: 'p',
      text: "This guide covers the Geofleet Google Sheets integration end to end: the column template, how ingestion works, what gets written back, the daily workflow, the data-quality traps that cause the most trouble, and an honest view of when a sheet is enough versus when it is time for an OMS. If you already know you have outgrown the spreadsheet, the migration post linked at the end is the next step.",
    },

    {
      type: 'h2',
      text: 'Why a spreadsheet is a legitimate order source',
    },
    {
      type: 'p',
      text: "Software vendors like to treat spreadsheets as the enemy. They are not; they are the most widely understood data entry tool in the world, and for ad-hoc B2B orders, one-off event deliveries, or a business at 30-80 orders a day they are often the right choice. The problems with sheets are not about capturing orders. They are about what happens after: nobody assigns drivers automatically, nobody tracks, and nobody writes the outcome back. Those are exactly the parts a dispatch platform handles.",
    },
    {
      type: 'ul',
      items: [
        'Zero training: the people entering orders already know Sheets.',
        'Instant setup: no product catalogue, no checkout, no ERP project. Copy the template and start.',
        'Flexible: a new column for a customer-specific field takes seconds, not a change request.',
        'Shared by default: the ops lead, the sales rep, and the finance person can all see the same rows.',
        'Good enough for ad-hoc volume: seasonal spikes, event logistics, or B2B accounts that order by email.',
      ],
    },

    {
      type: 'h2',
      text: 'The column template',
    },
    {
      type: 'p',
      text: "The integration reads one tab of one sheet and expects a header row. Column names can be mapped during setup, so you do not have to rename what you have, but the seven core columns below need to exist in some form. The write-back columns are added by Geofleet if they are missing.",
    },
    {
      type: 'table',
      caption: 'Google Sheets delivery template: input columns and write-back columns',
      headers: ['Column', 'Direction', 'Format and notes'],
      rows: [
        ['Order ID', 'Input (required)', 'Unique per row. Used to de-duplicate and match updates; never reuse an ID for a new order'],
        ['Customer name', 'Input (required)', 'Free text; appears on the tracking page and proof of delivery'],
        ['Phone', 'Input (required)', 'One number, with country code. Drives SMS notifications and the Captain call button'],
        ['Address', 'Input (required)', 'Single cell: street, unit, area, city, postcode. Avoid splitting across merged cells'],
        ['Delivery window', 'Input (optional)', 'Date plus start-end time, for example 2026-06-04 14:00-17:00. Blank means default window'],
        ['COD amount', 'Input (optional)', 'Number only; blank or 0 for prepaid. Drives cash management'],
        ['Notes', 'Input (optional)', 'Gate code, floor, fragile, call before arrival. Shown on the stop card'],
        ['Hub / pickup', 'Input (optional)', 'Needed only for multi-hub operations; maps to a Geofleet hub'],
        ['Status', 'Write-back', 'New, Dispatched, Out for delivery, Delivered, Failed, Cancelled'],
        ['Captain', 'Write-back', 'Assigned Captain name (or your tenant term for drivers)'],
        ['ETA', 'Write-back', 'Live estimated arrival, updated as the route progresses'],
        ['Tracking link', 'Write-back', 'Branded tracking URL for the customer'],
        ['POD link', 'Write-back', 'Link to the proof of delivery (photo, signature, OTP record)'],
        ['Delivered at', 'Write-back', 'Timestamp on completion; blank until then'],
      ],
    },
    {
      type: 'callout',
      variant: 'tip',
      title: 'One row, one delivery',
      text: "Resist the urge to put multiple parcels for one address on separate rows unless they are genuinely separate deliveries. Each row becomes one stop. If a customer ordered three items, describe them in Notes or a Contents column and keep one row.",
    },

    {
      type: 'h2',
      text: 'How the Sheets integration works: rows in, status out',
    },
    {
      type: 'h3',
      text: 'Ingest',
    },
    {
      type: 'p',
      text: "You connect the sheet from the Integrations card under Developer settings, authorise access, choose the tab, and map columns. From then on Geofleet watches the sheet. A new row with an Order ID that has not been seen becomes a new delivery order. A changed row (new address, new window) updates the existing order. Rows with a missing required field are not imported; instead the Status column is set to Needs attention with the reason, so the person who entered the row sees the problem where they entered it.",
    },
    {
      type: 'h3',
      text: 'Dispatch',
    },
    {
      type: 'p',
      text: "Imported orders enter the same pool as orders from Shopify, Acumatica, or the API. Auto Allocation assigns them to Captains by zone, capacity, and window, or the Smart Planner batches them into routes for the next wave. The dispatcher no longer reads the sheet to decide who goes where; they read the sheet to see what happened.",
    },
    {
      type: 'h3',
      text: 'Write-back',
    },
    {
      type: 'p',
      text: "As each order progresses, Geofleet writes Status, Captain, ETA, Tracking link, POD link, and Delivered at back to the row. Because these are ordinary cells, everything the team already does with Sheets keeps working: conditional formatting to colour rows by status, a filter view of failed deliveries, a pivot for deliveries per Captain, or a chart on a dashboard tab. Nothing about the write-back is proprietary.",
    },
    {
      type: 'h3',
      text: 'Row states',
    },
    {
      type: 'p',
      text: "The Status column follows a small state machine: New (imported, unassigned), Dispatched (Captain assigned), Out for delivery (Captain has started the stop), Delivered, Failed (attempted, with a reason), and Cancelled (row marked cancelled in the sheet, or cancelled in Geofleet). Needs attention sits outside the flow for rows that could not be imported. A row never goes backwards on its own; a failed delivery that is retried gets a new attempt on the same row.",
    },

    {
      type: 'infographic',
      image: '/images/blog/blog-info-sheets-dispatch.svg',
      alt: 'Two-lane diagram of one sheet: the columns you fill (order ID, customer and phone, address, time window) flow in as rows, and the columns Geofleet fills back (status, Captain, ETA, proof link) come out as results into the same rows',
      caption: 'Rows in, results out. The same sheet the team entered orders into becomes the live dispatch board.',
    },

    {
      type: 'h2',
      text: 'Using Google Sheets as a delivery route planner day to day',
    },
    {
      type: 'p',
      text: "A typical daily rhythm for a 60-order operation looks like this. The point is that the sheet remains the front door for orders while Geofleet does the work behind it.",
    },
    {
      type: 'ol',
      items: [
        'Morning: orders from overnight emails and forms are entered or pasted into the sheet. Needs attention rows are fixed within minutes because the reason is right there.',
        'Cut-off: the Smart Planner builds routes from all New rows for the morning wave; Captains see their routes on the driver app. Status flips to Dispatched.',
        'During the day: ad-hoc orders are added as rows and auto allocation slots them onto the nearest Captain with capacity. ETAs update live in the sheet.',
        'Exceptions: a Failed status with reason appears the moment a Captain records it; the dispatcher calls the customer and either re-queues (new window on the same row) or cancels.',
        'End of day: filter Delivered rows with a COD amount and compare against cash management; open the POD link for any row a customer queries.',
      ],
    },
    {
      type: 'quote',
      text: "The thing that surprised us was that nobody had to change tools. The sales team kept adding rows the way they always had. The only difference was that the rows started filling themselves in.",
      attribution: 'Owner, regional water delivery business',
    },

    {
      type: 'h2',
      text: 'Data-quality traps and how AI address cleanup helps',
    },
    {
      type: 'p',
      text: "Almost every failed delivery in a spreadsheet operation traces back to a data problem that existed before the driver left. These are the ones that come up most, and what to do about each.",
    },
    {
      type: 'h3',
      text: 'Bad or incomplete addresses',
    },
    {
      type: 'p',
      text: "Missing unit numbers, a landmark instead of a street, two addresses in one cell, or the city omitted because 'everyone knows'. Geofleet geocodes every address on import and AI address cleanup normalises it: it separates the unit from the street, recognises common landmark phrasing, flags addresses that geocode to a wrong area or to a low-confidence point, and asks for confirmation rather than sending a Captain to a guess. Rows that fail cleanup are marked Needs attention with the specific issue.",
    },
    {
      type: 'h3',
      text: 'Merged cells and layout tricks',
    },
    {
      type: 'p',
      text: "Merged header cells, a customer name spanning two rows, subtotal rows inside the data, or colour used to mean something. None of these survive ingestion. Keep one header row, one order per row, and use a Status or Notes column rather than colour to carry meaning. If you need a pretty view for management, build it on a second tab that references the data tab.",
    },
    {
      type: 'h3',
      text: 'Phone formats',
    },
    {
      type: 'p',
      text: "Numbers with spaces, dashes, brackets, a leading zero instead of a country code, or two numbers in one cell separated by a slash. The integration normalises the common cases to a single international-format number, but two numbers in one cell will always be a guess. Put the second number in Notes. Also watch for Sheets stripping a leading zero or converting a number to scientific notation; formatting the column as plain text avoids it.",
    },
    {
      type: 'h3',
      text: 'Reused or blank order IDs',
    },
    {
      type: 'p',
      text: "A blank Order ID cannot be de-duplicated, and a reused one will update last week's delivery instead of creating a new one. Use a formula that combines the date and the row number, or a simple incrementing sequence, and never edit an ID after the row has been dispatched.",
    },
    {
      type: 'callout',
      variant: 'info',
      title: 'Protect the write-back columns',
      text: "Use Sheets' range protection on the Status, Captain, ETA, Tracking link, POD link, and Delivered at columns so a well-meaning colleague cannot overwrite them. If someone needs to cancel an order, they set a Cancel flag in an input column rather than editing Status.",
    },

    {
      type: 'h2',
      text: 'When a sheet is enough, and when to move to an OMS',
    },
    {
      type: 'p',
      text: "Sheets fail gradually, not suddenly. The table gives the signals that usually mean an operation has outgrown the spreadsheet as its order source, and what the natural next step is. Note that moving the order source does not change how dispatch works; Geofleet keeps running the deliveries either way.",
    },
    {
      type: 'table',
      caption: 'Is a Google Sheet still the right order source?',
      headers: ['Signal', 'Sheet is fine', 'Time to move', 'Typical next step'],
      rows: [
        ['Daily volume', 'Under roughly 100 orders', 'Consistently above that, or spiky', 'Native connector (Shopify) or OMS'],
        ['Who enters orders', 'One or two people', 'Customers, many reps, or several channels', 'E-commerce platform or order portal'],
        ['Order edits', 'Rare', 'Frequent edits after dispatch', 'System with edit webhooks'],
        ['Returns and refunds', 'Occasional, handled manually', 'Routine', 'OMS or ERP with return documents'],
        ['Finance needs', 'A monthly export is enough', 'Invoicing on delivery, line-level reconciliation', 'ERP connector (for example Acumatica)'],
        ['Hubs', 'One', 'Several, with transfers', 'OMS with location awareness'],
        ['Audit requirements', 'Light', 'Regulated goods, contractual proof', 'System of record with access control'],
      ],
    },
    {
      type: 'p',
      text: "Many operations keep the sheet even after adding a proper order source. A distributor might run its regular customers through an ERP integration and still use a sheet for one-off deliveries, samples, and internal transfers. The comparison post on choosing between Shopify, Acumatica, and Sheets covers running more than one source at once.",
    },

    {
      type: 'h2',
      text: 'The next step',
    },
    {
      type: 'p',
      text: "If the signals in the table above are already familiar, the good news is that the sheet has done its job: it proved there is a delivery business here worth systematising. The migration guide, from spreadsheets to a delivery platform, walks through moving the order source without disrupting Captains or customers. If the sheet is still working, connect it, protect the write-back columns, fix the data traps, and let the rows fill themselves in.",
    },

    {
      type: 'relatedReading',
      heading: 'Related reading',
      links: [
        { label: 'From spreadsheets to a delivery platform: the migration guide', to: '/blog/spreadsheets-to-delivery-platform' },
        { label: 'Shopify vs Acumatica vs Google Sheets: which order source fits?', to: '/blog/shopify-vs-acumatica-vs-google-sheets-order-source-for-delivery' },
        { label: 'Auto allocation explained', to: '/blog/auto-allocation-explained-orders-assigned-without-a-dispatcher' },
        { label: 'MCP for dispatch teams: real query examples', to: '/blog/mcp-server-for-dispatch-teams-real-query-examples' },
        commonLinks.routeOpt,
      ],
    },
    {
      type: 'ctaRow',
      items: [ctaTrial, ctaDemo],
    },
  ],
  faq: [
    {
      question: 'Can I really use Google Sheets as a delivery route planner?',
      answer: "Yes, as the order source. The sheet holds the orders; Geofleet reads the rows, assigns Captains, plans routes, tracks deliveries, and writes status, ETA, and proof of delivery links back to the same rows. The sheet becomes the live view rather than the planning engine.",
    },
    {
      question: 'What columns does the Google Sheets delivery template need?',
      answer: "Required: Order ID, customer name, phone, and address. Optional: delivery window, COD amount, notes, and hub. Geofleet adds write-back columns for Status, Captain, ETA, Tracking link, POD link, and Delivered at if they do not exist.",
    },
    {
      question: 'How does status get written back to the sheet?',
      answer: "As each delivery progresses, Geofleet updates the row's Status, Captain, ETA, tracking link, proof of delivery link, and delivered timestamp. These are ordinary cells, so conditional formatting, filters, and pivots work on them as usual.",
    },
    {
      question: 'What happens to rows with a bad address or missing phone number?',
      answer: "They are not dispatched. The Status column is set to Needs attention with the reason, so whoever entered the row can fix it in place. AI address cleanup normalises common address problems automatically and flags low-confidence geocodes.",
    },
    {
      question: 'Can several people edit the sheet at once?',
      answer: "Yes, Sheets handles concurrent editing. Protect the write-back columns so they are not overwritten, and use a Cancel flag column instead of editing Status directly. Reused or blank Order IDs are the main risk with many editors.",
    },
    {
      question: 'When should I move from Google Sheets to a proper OMS?',
      answer: "Common signals are consistent volume above roughly 100 orders a day, orders entered by customers or many reps, frequent post-dispatch edits, routine returns, invoicing on delivery, or multiple hubs. The delivery side does not change when you swap the order source.",
    },
  ],
},
{
  slug: 'shopify-vs-acumatica-vs-google-sheets-order-source-for-delivery',
  title: 'Shopify vs Acumatica vs Google Sheets: Which Order Source Fits Your Delivery Operation?',
  excerpt: 'A decision framework for choosing the order management integration for delivery: e-commerce platform, ERP, or spreadsheet, by business type, volume, data ownership, write-back needs, and team size.',
  date: '2026-06-24',
  category: 'Integrations',
  author: 'Ankish Agarwal',
  readTime: '10 min read',
  image: 'https://cdn.geofleet.ai/blog-hero-order-source-compare.png',
  intro: 'Three very different systems can feed a dispatch platform. This post compares Shopify, Acumatica, and Google Sheets as order sources for last-mile delivery, gives a decision framework, and explains why many operations end up running more than one.',
  seo: {
    documentTitle: 'Shopify vs Acumatica vs Sheets for Delivery | Geofleet',
    metaDescription: 'Shopify, Acumatica, or Google Sheets: which order management integration for delivery fits you? A decision framework by business type, volume, and write-back.',
  },
  keyTakeaways: [
    { lead: 'The order source should be wherever the order is born.', text: 'D2C orders are born in Shopify, B2B orders in the ERP, ad-hoc orders in a sheet. Connect the source; do not force orders through a system they do not belong in.' },
    { lead: 'Write-back needs decide more than volume does.', text: 'If finance must invoice on delivery, you need an ERP-grade integration. If customers need a tracking link in a store email, you need the e-commerce connector.' },
    { lead: 'Data ownership is a real criterion, not a checkbox.', text: 'The system of record for customers, prices, and inventory should stay where it is; the delivery platform executes and reports back.' },
    { lead: 'Running two sources is normal.', text: 'Shopify for retail plus Sheets for wholesale, or Acumatica for contract customers plus Sheets for samples, is a common and sensible setup.' },
    { lead: 'Dispatch does not change when the source changes.', text: 'Captains, routing, tracking, and proof of delivery behave identically regardless of where the order came from, which makes migrating sources low-risk.' },
  ],
  body: [],
  sections: [
    {
      type: 'p',
      text: "Choosing an order management integration for delivery is less about which system is best and more about which system already holds the truth about your orders. A D2C brand's truth lives in Shopify. A distributor's truth lives in the ERP, often Acumatica. A courier taking phone orders has its truth in a Google Sheet. Each can be connected to Geofleet as an order source, and each carries different expectations about volume, ownership, and what needs to flow back.",
    },
    {
      type: 'p',
      text: "This post is for the mid-funnel reader: you know you need dispatch software connected to your orders, and you are deciding what to connect. It compares the three sources side by side, gives a decision framework, and covers the case, more common than you might think, where the right answer is two of them. The three detailed setup guides are linked throughout.",
    },

    {
      type: 'h2',
      text: 'What an order management integration for delivery has to do',
    },
    {
      type: 'p',
      text: "Before comparing sources, it helps to be precise about the job. Any order source connected to a delivery platform has to handle five things, and the sources differ in how well they handle each.",
    },
    {
      type: 'ul',
      items: [
        'Ingest: get new orders into dispatch with address, contact, window, contents, and payment state, without retyping.',
        'Update: propagate edits and cancellations so the Captain never delivers stale instructions.',
        'Write back: return status, timestamps, proof of delivery, and tracking links to the place people look.',
        'Identify: carry one stable reference (order number, shipment number, row ID) through the whole lifecycle.',
        'Scope: only send deliverable orders, from the right location, to the right hub.',
      ],
    },
    {
      type: 'p',
      text: "Shopify and Acumatica have native connectors in Geofleet that handle all five with configuration. Google Sheets handles them too, with the understanding that update and identify depend on the discipline of the people editing the sheet.",
    },

    {
      type: 'h2',
      text: 'The three order sources at a glance',
    },
    {
      type: 'table',
      caption: 'Shopify vs Acumatica vs Google Sheets as an order source for last-mile delivery',
      headers: ['Criterion', 'Shopify', 'Acumatica (ERP)', 'Google Sheets'],
      rows: [
        ['Best for', 'D2C e-commerce, local retail with online checkout', 'B2B distributors, manufacturers, contract logistics', 'Couriers, local services, ad-hoc and seasonal volume'],
        ['Where the order is born', 'Customer checkout', 'Sales order entered by reps or EDI', 'Manual entry, forms, phone, email'],
        ['Trigger into dispatch', 'Order created or paid webhook', 'Shipment reaches Open or Confirmed', 'New row with an Order ID'],
        ['Typical volume fit', 'Tens to thousands per day', 'Tens to hundreds of shipments per day, higher value', 'Under roughly 100 per day'],
        ['Edits and cancellations', 'Automatic via webhooks', 'Automatic via shipment changes', 'Manual; row edits are picked up, discipline required'],
        ['Write-back', 'Fulfillment status, tracking URL in store emails', 'Delivered time, POD, quantities, distance, exceptions; invoice release', 'Status, Captain, ETA, tracking and POD links in the row'],
        ['Finance loop', 'Payment already captured; COD reconciled in Geofleet', 'Invoice on delivery, partials, returns, freight recharge', 'Export or manual; COD reconciled in Geofleet'],
        ['Data ownership', 'Shopify owns customer and order; Geofleet owns delivery', 'ERP is system of record for everything commercial', 'The sheet, and whoever edits it'],
        ['Setup effort', 'Under a day', 'Days: process decisions plus configuration', 'Under an hour'],
        ['Team size sweet spot', 'Small to large with online sales', 'Mid to large with a finance function', 'Small teams, one or two order entry people'],
        ['Multi-location', 'Locations map to hubs', 'Warehouses map to hubs', 'Optional hub column'],
        ['Main risk', 'Non-delivery orders leaking into dispatch', 'Handoff and partial-delivery rules undefined', 'Data quality and reused IDs'],
      ],
    },
    {
      type: 'callout',
      variant: 'info',
      title: 'What does not differ',
      text: "Once an order is in Geofleet, the source is irrelevant to execution. Auto allocation, route planning, the driver app, live tracking, proof of delivery, and reports treat a Shopify order, an Acumatica shipment, and a sheet row identically. That is what makes it safe to start with one source and change later.",
    },

    {
      type: 'infographic',
      image: '/images/blog/blog-info-order-source-compare.svg',
      alt: 'Three cards for Shopify, Acumatica, and Google Sheets as delivery order sources, each showing who it is best for and what it writes back, all converging on one Geofleet dispatch layer',
      caption: 'Three sources, one dispatch layer. Pick the source where the order is born and the write-back you need; execution is identical after that.',
    },

    {
      type: 'h2',
      text: 'A decision framework',
    },
    {
      type: 'p',
      text: "Work through the five questions in order. Most operations have a clear answer by the third.",
    },
    {
      type: 'h3',
      text: '1. Where is the order born?',
    },
    {
      type: 'p',
      text: "This is the strongest signal. If customers place orders in a storefront, the storefront is the source. If reps enter sales orders in the ERP, the ERP is the source. If orders arrive by phone and someone types them, the sheet (or whatever the typist uses) is the source. Moving the birth of the order into a different system just to feed delivery creates double entry, which is the problem you were trying to remove.",
    },
    {
      type: 'h3',
      text: '2. What is the daily volume and how spiky is it?',
    },
    {
      type: 'p',
      text: "Shopify and Acumatica handle volume without human effort scaling alongside it. A sheet scales with the number of people typing. Under roughly 100 orders a day with one or two entry people, a sheet is comfortable. Above that, or when volume spikes unpredictably, the source needs to be a system that captures orders without a person in the loop.",
    },
    {
      type: 'h3',
      text: '3. Who owns the data?',
    },
    {
      type: 'p',
      text: "Ask where the master record for customers, prices, and stock lives, and who is accountable for it. If the answer is an ERP with a finance team behind it, that system must remain the system of record and the integration has to respect it: Geofleet executes and reports back, and does not become a second place where customer data is edited. If the answer is 'the sheet', ownership is looser, which is fine as long as everyone knows it.",
    },
    {
      type: 'h3',
      text: '4. What needs to flow back?',
    },
    {
      type: 'p',
      text: "This is where the sources diverge most. A D2C brand needs the store to show delivered and the customer email to carry a tracking link; the Shopify connector does that. A distributor needs delivered timestamps, proof, and quantities on the shipment so invoicing can run; that is ERP-grade write-back and it is what the Acumatica connector is built for. A courier needs to see status and the POD link next to the order; the sheet write-back does that. If finance cannot close without delivery data, you need the ERP path.",
    },
    {
      type: 'h3',
      text: '5. How big is the team, and who will own the integration?',
    },
    {
      type: 'p',
      text: "A one-person ops team should not be running an ERP integration project. A company with an Acumatica administrator and a controller should not be running invoicing from a sheet. Match the source to the people who will look after it. All three connectors are configured rather than coded, but the ERP path involves process decisions (handoff, partials, holds) that need an owner.",
    },
    {
      type: 'quote',
      text: "We spent a month debating whether to put wholesale orders through the web store so everything would be in one place. In the end we connected the ERP for wholesale and left the store for retail. Both feed the same drivers. The debate was the expensive part.",
      attribution: 'Logistics lead, specialty food distributor',
    },

    {
      type: 'h2',
      text: 'Profiles: which source fits which business',
    },
    {
      type: 'h3',
      text: 'D2C e-commerce and local retail',
    },
    {
      type: 'p',
      text: "Orders come from a storefront, are prepaid or COD, and customers expect a tracking link in the store's own emails. Shopify is the source. Key setup decisions are the trigger (created or paid), scoping to local delivery shipping methods only, and mapping locations to hubs. The Shopify setup guide covers all three. Grocery, flowers, pharmacies, and meal delivery mostly land here.",
    },
    {
      type: 'h3',
      text: 'B2B distributor or manufacturer',
    },
    {
      type: 'p',
      text: "Orders are sales orders in the ERP, deliveries are shipments with line quantities, and finance invoices on delivery. Acumatica (or another ERP) is the source, and the write-back is the point. Key decisions are the shipment trigger, warehouse handoff, and the partial-delivery policy. The Acumatica guide goes through each. Building supplies, automotive parts, medical supplies, and manufacturing typically fit this profile.",
    },
    {
      type: 'h3',
      text: 'Local courier or service business',
    },
    {
      type: 'p',
      text: "Orders arrive by phone, WhatsApp, email, or a form, and a dispatcher currently reads them off a list. A Google Sheet is the source, at least to start. Key decisions are the column template, protecting write-back columns, and the cutover point to something bigger. The Sheets guide covers the template and the data traps. Couriers, laundry, water delivery, and field service often start here.",
    },

    {
      type: 'h2',
      text: 'You can run more than one',
    },
    {
      type: 'p',
      text: "Because every source feeds the same dispatch pool, there is no technical reason to pick only one, and often a strong business reason not to. Orders from all connected sources are allocated together, so a Captain's route can include a Shopify order, an ERP shipment, and a sheet row back to back without anyone noticing the difference. Common combinations:",
    },
    {
      type: 'ul',
      items: [
        'Shopify for D2C retail plus Google Sheets for ad-hoc B2B or wholesale orders that come in by email.',
        'Acumatica for contract customers and scheduled shipments plus Sheets for samples, returns pickups, and internal transfers.',
        'Shopify for the online store plus Acumatica for the trade counter and wholesale side of the same business.',
        'Any of the above plus the batch API for a custom app or marketplace feed.',
      ],
    },
    {
      type: 'p',
      text: "The practical rule is that each order must have exactly one source. Do not enter a Shopify order into the sheet as well, and do not create an ERP shipment for a store order 'so finance can see it'; use the write-back instead. If you need to ask questions across sources (which Shopify orders and which ERP shipments are running late for the same account?), the Geofleet MCP server lets an AI assistant query all of them together; the MCP query examples post shows what those questions look like in practice.",
    },
    {
      type: 'callout',
      variant: 'tip',
      title: 'Start with the source that carries the most volume',
      text: "Connect the source responsible for the majority of daily orders first, run it for two weeks, then add the second. Two integrations going live on the same day doubles the number of things to debug in week one.",
    },

    {
      type: 'h2',
      text: 'What to check before committing',
    },
    {
      type: 'ol',
      items: [
        'Can you list, for one typical order, every field the Captain and the customer need, and confirm the source carries it? Missing delivery windows and unit numbers are the usual gaps.',
        'Who fixes a bad address, and where? The answer should be a person and a screen, whether that is the Shopify order, the Acumatica shipment, or the sheet row.',
        'What must be true in the source system when the delivery is done? Fulfilled, invoiced, or a green cell. That defines the write-back you need.',
        'What happens to an order that changes after dispatch? Test it deliberately during the pilot.',
        'Who owns the integration after go-live? Name them before, not after.',
      ],
    },
    {
      type: 'p',
      text: "If you are still unsure after this, the tie-breaker is data ownership. Connect the system that finance and customer service would trust in a dispute, and let Geofleet report back to it. For the technical options behind each connector, including MCP, webhooks, and CSV fallbacks, see the guide on connecting an ERP without custom code.",
    },

    {
      type: 'relatedReading',
      heading: 'Related reading',
      links: [
        { label: 'Shopify delivery management integration: setup guide', to: '/blog/shopify-delivery-management-integration-sync-orders-to-dispatch' },
        { label: 'Acumatica last-mile integration: closing the ERP loop', to: '/blog/acumatica-last-mile-delivery-integration-erp-to-dispatch' },
        { label: 'Google Sheets as a lightweight OMS', to: '/blog/google-sheets-delivery-dispatch-lightweight-oms' },
        { label: 'Geofleet MCP: connect Shopify, HubSpot, and Sheets', to: '/blog/geofleet-mcp-integrations-shopify-hubspot-sheets' },
        { label: 'Integrations directory', to: '/integrations' },
      ],
    },
    {
      type: 'ctaRow',
      items: [ctaDemo, ctaTrial, ctaAgents],
    },
  ],
  faq: [
    {
      question: 'Which order source is best for last-mile delivery?',
      answer: "The one where your orders are born. Storefront orders belong in Shopify, B2B sales orders in the ERP such as Acumatica, and phone or email orders in a Google Sheet. Forcing orders into a different system to feed delivery creates double entry.",
    },
    {
      question: 'Can I connect Shopify and Acumatica to Geofleet at the same time?',
      answer: "Yes. All connected sources feed the same dispatch pool, so a Captain's route can include Shopify orders and ERP shipments together. Each order should have exactly one source so it is not created twice.",
    },
    {
      question: 'Is Google Sheets a serious option or just a stopgap?',
      answer: "It is a serious option for ad-hoc, seasonal, or low-volume orders entered by one or two people, and many operations keep a sheet alongside an e-commerce or ERP connector permanently. It becomes a stopgap when volume, editors, or finance needs grow beyond what a sheet can carry.",
    },
    {
      question: 'How do the write-backs differ between the three sources?',
      answer: "Shopify receives fulfillment status and a tracking URL that appears in the store's customer emails. Acumatica receives delivered timestamps, proof of delivery, line quantities, distance, and exceptions, and can release the invoice. Google Sheets receives status, Captain, ETA, tracking and POD links in the row.",
    },
    {
      question: 'Does changing the order source later disrupt drivers?',
      answer: "No. Captains, routing, tracking, proof of delivery, and reports behave the same regardless of source. Swapping a sheet for a Shopify or ERP connector changes where orders come from, not how they are delivered.",
    },
    {
      question: 'What if my orders come from a custom app or marketplace?',
      answer: "Use the Geofleet webhooks and batch API alongside any of the native connectors. The comparison in this post still applies: the question is where the order is born and what has to flow back.",
    },
  ],
},
{
  slug: 'connect-erp-to-delivery-platform-without-custom-code',
  title: 'How to Connect Your ERP to a Delivery Platform Without Custom Code',
  excerpt: 'Four ERP delivery integration paths ranked by effort: native connector, MCP, webhooks plus batch API, and CSV or Sheets. Plus data ownership, idempotency, error handling, sandbox testing, and a 4-week rollout plan.',
  date: '2026-07-15',
  category: 'Integrations',
  author: 'Aditya Singh',
  readTime: '11 min read',
  image: 'https://cdn.geofleet.ai/blog-hero-erp-no-code.png',
  intro: 'A head-term guide to ERP delivery integration for operations and IT leads who want the last mile connected to the system of record without commissioning a custom build.',
  seo: {
    documentTitle: 'ERP Delivery Integration Without Custom Code | Geofleet',
    metaDescription: 'Connect an ERP to last-mile delivery without custom code, four ways: native connector, MCP, webhooks and batch API, CSV. Plus idempotency and a 4-week rollout.',
  },
  keyTakeaways: [
    { lead: 'There are four paths, and effort is the axis that matters.', text: 'Native connector, MCP, webhooks plus batch API, and CSV or Sheets each connect an ERP to delivery; they differ in setup effort, real-time behaviour, and how much you can shape.' },
    { lead: 'The ERP stays the system of record in every path.', text: 'Delivery executes and reports back. The moment the delivery platform starts editing customer or price data, you have two systems of record and a reconciliation problem.' },
    { lead: 'Idempotency and error handling are not optional extras.', text: 'ERPs and webhooks both retry. Every message must carry a stable key and be safe to receive twice.' },
    { lead: 'Test in a sandbox with deliberately broken data.', text: 'A duplicate shipment, a short delivery, a cancelled order after dispatch, and a bad address should all be part of the test plan before go-live.' },
    { lead: 'Four weeks is a realistic no-code rollout.', text: 'One week each for mapping, sandbox, parallel running, and cutover, with a named owner on each side.' },
  ],
  body: [],
  sections: [
    {
      type: 'p',
      text: "ERP delivery integration used to mean a six-figure project: consultants, a middleware licence, and custom code that nobody wanted to touch afterwards. That is no longer the default. Between native connectors, the Model Context Protocol, standard webhooks with a batch API, and plain CSV, most operations can connect their ERP to a delivery platform with configuration rather than code. The choice is about effort, real-time requirements, and how much you need to shape the flow.",
    },
    {
      type: 'p',
      text: "This guide lays out the four paths in order of effort, using Geofleet and Acumatica as the worked example for the native path, then covers the parts that decide whether an integration survives contact with production: data ownership, idempotency, error handling, sandbox testing, and a four-week rollout plan. It is written for the IT lead or operations director who owns the outcome, not the developer who might one day write against the API.",
    },

    {
      type: 'h2',
      text: 'What an ERP delivery integration needs to move',
    },
    {
      type: 'p',
      text: "Regardless of path, the same data crosses the boundary. Outbound from the ERP: shipments (or sales orders) with ship-to address and contact, lines and quantities, requested dates or windows, freight terms, delivery instructions, and payment terms including any cash to collect. Inbound to the ERP: delivered timestamp, proof of delivery, delivered quantities, exceptions, measured distance, and a tracking reference. Any path that cannot carry the inbound half is a one-way feed, not an ERP last mile integration.",
    },
    {
      type: 'ul',
      items: [
        'Outbound: shipment or order header, ship-to, lines, window, instructions, COD amount, warehouse or hub.',
        'Inbound: delivered time, POD (signature, photo, name), quantities per line, exception codes, distance, tracking URL.',
        'Both directions: a stable identifier, usually the ERP shipment number, carried unchanged end to end.',
        'Lifecycle events: edits and cancellations after the shipment has been handed to delivery.',
      ],
    },

    {
      type: 'h2',
      text: 'Four integration paths, ranked by effort',
    },
    {
      type: 'h3',
      text: 'Path 1: Native connector (lowest effort)',
    },
    {
      type: 'p',
      text: "A native connector is a pre-built integration between the delivery platform and a specific ERP, configured from a settings screen. In Geofleet, the Acumatica connector lives in the Integrations card under Developer settings: you connect the Acumatica instance, choose which shipment status triggers a delivery, map fields, and enable write-back. The connector already knows the ERP's document model, so partial deliveries, returns, and invoice release are handled by configuration options rather than custom logic. The Acumatica guide linked below walks through it in detail.",
    },
    {
      type: 'p',
      text: "Choose this path if a connector exists for your ERP. The trade-off is flexibility: you get the flows the connector supports, which cover the vast majority of distribution and manufacturing cases, and anything exotic needs one of the other paths alongside it.",
    },
    {
      type: 'h3',
      text: 'Path 2: MCP (low effort, AI-driven)',
    },
    {
      type: 'p',
      text: "The Model Context Protocol lets an AI assistant query and act across systems that expose MCP tools. If your ERP and Geofleet both do, an assistant can answer 'which shipments confirmed today have not been dispatched?' or 'create delivery orders for these five shipments' from a single instruction, with no integration code. MCP is not a replacement for a connector on high-volume, always-on flows; it is the right tool for cross-system questions, exception handling, and lower-volume actions where a human is in the loop. The Geofleet MCP intro post explains the protocol and its governance model, and the dispatch team query examples post shows real prompts.",
    },
    {
      type: 'p',
      text: "Because MCP calls are made by an AI model, the bring-your-own-LLM option matters here: Geofleet lets a tenant choose the model provider and supply their own API key under the LLM model providers section, so prompts and delivery data go to the provider account the client controls. Teams with data residency requirements should read the data residency post before enabling this path.",
    },
    {
      type: 'h3',
      text: 'Path 3: Webhooks plus batch API (moderate effort, most flexible)',
    },
    {
      type: 'p',
      text: "Most ERPs can send an outbound notification when a document changes, and most can receive an inbound update through a REST endpoint or import. Geofleet's webhooks push delivery events (assigned, out for delivery, delivered, failed) to any URL, and the batch API accepts orders in bulk. Connecting the two usually needs a light integration layer: an automation tool, a small serverless function, or the ERP's own business-events feature. It is not custom code in the traditional sense, but someone has to own the mapping. This is the path for ERPs without a native connector, or for flows the connector does not cover. The webhooks and batch API post goes into the mechanics.",
    },
    {
      type: 'h3',
      text: 'Path 4: CSV or Google Sheets (lowest tech, highest manual effort)',
    },
    {
      type: 'p',
      text: "Every ERP can export a CSV of shipments and import a CSV of results, and the Google Sheets integration turns that into a near-live loop: export shipments to a sheet on a schedule, Geofleet dispatches from the rows and writes status, timestamps, and POD links back, and the ERP imports the results. It is not real time and it depends on someone running the export, but it works on day one with zero integration work and is a reasonable fallback during an ERP migration or for a small warehouse the main connector does not cover. The Sheets guide has the template.",
    },

    {
      type: 'infographic',
      image: '/images/blog/blog-info-erp-no-code.svg',
      alt: 'Four ERP delivery integration paths stacked by setup effort from least to most: native connector (configure, no code), CSV or Sheets (scheduled, works day one), MCP (agent driven, flexible), and webhooks plus batch API (real time, built by IT)',
      caption: 'The four paths ranked by setup effort. A native connector is lowest effort and most real-time when one exists; the other three trade effort against timeliness and flexibility.',
    },

    {
      type: 'h2',
      text: 'Comparing the four paths',
    },
    {
      type: 'table',
      caption: 'ERP delivery integration paths compared',
      headers: ['Path', 'Setup effort', 'Real-time', 'Write-back depth', 'Flexibility', 'Best when'],
      rows: [
        ['Native connector (e.g. Acumatica)', 'Hours to a few days, configuration only', 'Seconds to minutes', 'Full: POD, timestamps, quantities, exceptions, invoice release', 'Fixed to supported flows', 'A connector exists for your ERP'],
        ['MCP', 'Hours; enable tools and scopes', 'On demand', 'Whatever the tools expose, with approval flows for writes', 'High for questions and exceptions', 'Cross-system queries and human-in-the-loop actions'],
        ['Webhooks + batch API', 'Days; someone owns the mapping', 'Seconds', 'Anything you map', 'Highest', 'No connector, or custom flows alongside one'],
        ['CSV / Sheets', 'Under an hour', 'Scheduled batches', 'Status, timestamps, links in the sheet', 'Low', 'Fallback, migration periods, small sites'],
      ],
    },
    {
      type: 'callout',
      variant: 'info',
      title: 'They combine',
      text: "A typical production setup is a native connector for the main shipment flow, MCP for questions and exception handling, and webhooks for one custom notification the connector does not send. Pick the primary path first, then add the others where they earn their keep.",
    },

    {
      type: 'h2',
      text: 'Data ownership, idempotency, and error handling',
    },
    {
      type: 'h3',
      text: 'Data ownership',
    },
    {
      type: 'p',
      text: "Decide, in writing, which system owns each entity. The ERP owns customers, prices, inventory, and the commercial document. The delivery platform owns the delivery: assignment, route, proof, and delivery-stage timestamps. Address corrections are the grey area: if a Captain discovers the ship-to is wrong, the fix should go back to the ERP customer record, not live only in Geofleet. Put that rule in the integration design and the write-back mapping.",
    },
    {
      type: 'h3',
      text: 'Idempotency',
    },
    {
      type: 'p',
      text: "ERPs re-send. Webhook systems retry on timeouts. Sheets get pasted twice. Every message must carry a stable key (the shipment number) and every receiver must treat a repeated key as an update, never a new record. Geofleet de-duplicates on the external reference across all four paths. Confirm your ERP side does the same for inbound results, so a delivered event received twice does not release two invoices.",
    },
    {
      type: 'h3',
      text: 'Error handling',
    },
    {
      type: 'p',
      text: "Three classes of error need three different responses. Validation errors (missing address, unknown warehouse) should stop the record and notify the person who can fix it, in the system where they work. Transient errors (a timeout) should retry with backoff and alert only if retries are exhausted. Business exceptions (short delivery, refusal) are not errors at all; they are outcomes that must be written back with a code so the ERP can apply its policy. A dashboard of records in each class, reviewed daily, is the minimum operating discipline.",
    },
    {
      type: 'callout',
      variant: 'tip',
      title: 'Never let the integration invent data',
      text: "If a required field is missing, hold the record and ask. An integration that fills in a default warehouse or a guessed window to keep things moving will produce deliveries nobody can explain later.",
    },

    {
      type: 'h2',
      text: 'Testing in a sandbox',
    },
    {
      type: 'p',
      text: "Both the ERP and the delivery platform should offer a non-production environment; Acumatica has sandbox tenants and Geofleet supports a test workspace. Do not test with real customers or real Captains. Build a fixture set of ten shipments that covers the awkward cases, and run the full loop on each before anyone sees a live order.",
    },
    {
      type: 'ol',
      items: [
        'A clean shipment: confirm every mapped field arrives and every write-back field returns.',
        'The same shipment sent twice: confirm exactly one delivery exists.',
        'A shipment edited after dispatch: confirm the Captain sees the change.',
        'A shipment cancelled after dispatch: confirm the stop is removed or flagged.',
        'A short delivery: confirm delivered quantities and the ERP partial policy.',
        'A refused delivery: confirm the exception code and photo land on the shipment.',
        'A bad address: confirm the record is held with a reason, not dispatched.',
        'A COD shipment: confirm the amount reaches the Captain and reconciles.',
        'Multi-warehouse: confirm each maps to the right hub.',
        'A write-back failure (revoke the API permission on purpose): confirm the alert fires and the retry succeeds once restored.',
      ],
    },
    {
      type: 'quote',
      text: "The test that saved us was sending the same shipment twice. In the old system that would have produced two deliveries and one very confused customer. Catching it in the sandbox cost an hour.",
      attribution: 'IT manager, automotive parts distributor',
    },

    {
      type: 'h2',
      text: 'A 4-week rollout plan',
    },
    {
      type: 'table',
      caption: 'Four-week no-code ERP delivery integration rollout',
      headers: ['Week', 'Focus', 'Outputs', 'Owner'],
      rows: [
        ['1', 'Design and mapping', 'Path chosen; field map; ownership rules; partial, return, and hold policies; identifier confirmed', 'Ops lead + ERP admin'],
        ['2', 'Sandbox', 'Connector configured; ten-shipment fixture run; error classes wired to alerts; Captain proof settings agreed', 'ERP admin + Geofleet admin'],
        ['3', 'Parallel run', 'One warehouse or route group live alongside the existing process; daily three-column check (timestamp, POD, invoice); fixes applied', 'Ops lead'],
        ['4', 'Cutover and expand', 'Old process retired for the pilot group; remaining warehouses added; MCP or webhooks added for gaps; runbook written', 'Ops lead + IT'],
      ],
    },
    {
      type: 'p',
      text: "The plan assumes a native connector or the Sheets path. Add a week for the webhooks path, mostly for the mapping layer. The most common cause of slippage is not technical; it is week-one policy decisions (what happens on a short delivery?) that nobody owns. Assign them by name.",
    },

    {
      type: 'h2',
      text: 'Security questions to settle before go-live',
    },
    {
      type: 'p',
      text: "An ERP integration hands a third party access to commercial data, so IT will rightly ask about it. The short list: least-privilege credentials scoped to shipments and the write-back fields only; API keys stored and rotated on a schedule; MFA on the accounts that administer both sides; audit logs for every write on both systems; and, for MCP, read-only scopes first with approval flows for any write. The security checklist post has twelve questions worth putting to any delivery vendor before the contract is signed.",
    },

    {
      type: 'relatedReading',
      heading: 'Related reading',
      links: [
        { label: 'Acumatica last-mile integration: closing the ERP loop', to: '/blog/acumatica-last-mile-delivery-integration-erp-to-dispatch' },
        { label: 'Webhooks and batch API for real-time OMS to dispatch sync', to: '/blog/webhooks-batch-api-real-time-sync-oms-dispatch' },
        { label: 'Geofleet MCP: connect Shopify, HubSpot, and Sheets', to: '/blog/geofleet-mcp-integrations-shopify-hubspot-sheets' },
        { label: 'Delivery software security checklist: 12 questions', to: '/blog/delivery-software-security-checklist-12-questions' },
        { label: 'Bring your own LLM: choose your AI model and API key', to: '/blog/bring-your-own-llm-logistics-choose-your-ai-model-and-api-key' },
      ],
    },
    {
      type: 'ctaRow',
      items: [ctaDemo, ctaTrial, ctaAgents],
    },
  ],
  faq: [
    {
      question: 'Can I integrate my ERP with a delivery platform without developers?',
      answer: "Usually, yes. A native connector such as Geofleet's Acumatica integration is configured from a settings screen. MCP and the Google Sheets path also need no code. The webhooks and batch API path needs someone to own a mapping layer, typically with an automation tool rather than custom software.",
    },
    {
      question: 'Which ERP delivery integration path is the most real-time?',
      answer: "Native connectors and webhooks both operate in seconds. MCP is on demand, triggered by an instruction. CSV or Sheets runs on whatever export schedule you set, so it is near-live at best.",
    },
    {
      question: 'What data should flow back from delivery to the ERP?',
      answer: "Delivered timestamp, proof of delivery, delivered quantities per line, exception codes, GPS-measured distance, and a tracking reference. These let the ERP invoice on delivery, handle partials and returns, and give sales and service visibility.",
    },
    {
      question: 'What is idempotency and why does it matter for ERP integration?',
      answer: "Idempotency means receiving the same message twice produces the same result as receiving it once. ERPs and webhooks retry, so every record needs a stable key such as the shipment number, and both sides must treat a repeat as an update rather than a new record.",
    },
    {
      question: 'How long does an ERP last mile integration take?',
      answer: "With a native connector or the Sheets path, four weeks is realistic: design and mapping, sandbox testing, a parallel run on one site, then cutover. Add a week for the webhooks path. Policy decisions such as partial-delivery handling are the usual cause of delay, not the technology.",
    },
    {
      question: 'Is MCP a replacement for an ERP connector?',
      answer: "No. MCP is for AI-driven cross-system questions and human-in-the-loop actions. High-volume, always-on shipment flows belong on a native connector or webhooks. Most production setups use MCP alongside a connector rather than instead of one.",
    },
  ],
},
];
